import { ConfigIniParser } from 'config-ini-parser';
import { useEffect } from 'react';
import { useSearchParams } from 'react-router';
import { toast } from 'sonner';

import { Loader } from '@/components/Loader';
import { useAuth } from '@/state/auth';
import { BackendStack } from '@/types';

export function LoginWithKeychainLink() {
  const configIniParser = new ConfigIniParser();

  const { isSignedIn, setCustomStack } = useAuth();
  const [urlParams] = useSearchParams();

  useEffect(() => {
    // If already signed in, redirect to home instead of clearing storage
    // (clearing storage here caused infinite loops by wiping credentials just set up)
    if (isSignedIn) {
      window.location.href = '/';
      return;
    }

    const timeout = setTimeout(() => {
      const keychainParam = urlParams.get('keychain');

      if (keychainParam) {
        try {
          // Decode the base64 keychain parameter
          const decodedKeychain = decodeURIComponent(keychainParam);

          // Parse the decoded content
          const config = configIniParser.parse(decodedKeychain);
          const sections = config.sections();

          if (sections.length > 0) {
            const backend = sections.shift() ?? '';

            const creds: BackendStack = {
              backend: config.get(backend, 'name').trim() as string,
              clientId: config.get(backend, 'client_id').trim() as string,
              api: config.get(backend, 'api').trim() as string,
              userPoolId: config.get(backend, 'user_pool_id').trim() as string,
            };

            let loginCreds: { username: string; password: string } = {
              username: '',
              password: '',
            };

            const hasLoginCreds =
              config.isHaveOption(backend, 'username') &&
              config.isHaveOption(backend, 'password');

            if (hasLoginCreds) {
              const username = config.get(backend, 'username').trim() as string;
              const password = config.get(backend, 'password').trim() as string;

              if (username && password) {
                loginCreds = {
                  username,
                  password,
                };
              }
            }

            setCustomStack(creds, loginCreds);
          }
        } catch (error) {
          if (error instanceof Error && error.message) {
            toast.error('Invalid keychain format from URL parameter', {
              description: error.message,
            });
          }
        }
      }
    }, 1000);

    return () => clearTimeout(timeout);
  }, []);

  return <Loader isLoading={true} type="spinner" />;
}
